import React from 'react';
import { FaBox } from 'react-icons/fa';
import {
  formatNamedRelationList,
  getProductImageSrc,
  summarizePackRows,
} from './cartUtils';

export default function CartProductModal({ product, show, onClose, specsLoading }) {
  if (!show || !product) return null;

  const imageSrc =
    getProductImageSrc(product) || product.mainImage || null;

  const itemNo = product.itemNo ?? product.item_no;
  const category = product.category;
  const productName = product.productName ?? product.product_name;
  const hsCode = product.hsCode ?? product.hs_code;
  const colors = formatNamedRelationList(product.color);
  const finishes = formatNamedRelationList(product.finish);
  const materials = formatNamedRelationList(product.material);
  const collectionsList = formatNamedRelationList(product.collection);
  const packTypes = formatNamedRelationList(product.packType ?? product.pack_type);
  const suitableFor = formatNamedRelationList(product.suitableFor ?? product.suitable_for);

  const lengthDim =
    product.lengthDimension ?? product.length_dimension ?? null;
  const breadthDim =
    product.breadthDimension ?? product.breadth_dimension ?? null;
  const heightDim =
    product.heightDimension ?? product.height_dimension ?? null;
  const dimUnit = product.dimensionUnit ?? product.dimension_unit;

  let dimensionsLine = null;
  if (
    lengthDim != null &&
    breadthDim != null &&
    heightDim != null &&
    dimUnit
  ) {
    dimensionsLine = `${lengthDim} × ${breadthDim} × ${heightDim} ${dimUnit}`;
  }

  const netWeight = product.netWeight ?? product.net_weight;
  const weightUnit = product.weightUnit ?? product.weight_unit;
  let weightLine = null;
  if (netWeight != null && weightUnit) {
    weightLine = `${netWeight} ${weightUnit}`;
  } else if (netWeight != null) {
    weightLine = String(netWeight);
  }

  const uomDisplay =
    product.uomCountLabel ??
    product.uom_count_label ??
    product.uomCountName ??
    (product.uomCount != null && product.uomCount !== ''
      ? String(product.uomCount)
      : product.uom_count != null && product.uom_count !== ''
        ? String(product.uom_count)
        : null);

  const innerPackLines = summarizePackRows(
    product.innerPacks ?? product.inner_packs,
  );
  const masterPackLines = summarizePackRows(
    product.masterPacks ?? product.master_packs,
  );

  const showPackingSection =
    (innerPackLines && innerPackLines.length > 0) ||
    (masterPackLines && masterPackLines.length > 0);

  const SpecRow = ({ label, value }) => {
    if (value == null || value === '') return null;
    return (
      <>
        <div className="collection-modal-spec-label">{label}</div>
        <div className="collection-modal-spec-value">{value}</div>
      </>
    );
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="collection-product-modal-title"
        className="collection-product-modal-panel"
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h4 id="collection-product-modal-title" style={{ margin: 0 }}>
            Product Details
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close product details"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        </div>

        {specsLoading ? (
          <p
            className="collection-modal-specs-loading"
            role="status"
            aria-live="polite"
          >
            Loading latest specifications…
          </p>
        ) : null}

        <div
          style={{
            marginBottom: '16px',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#f5f7fa',
            height: '250px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={itemNo ? String(itemNo) : 'Product'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <FaBox size={60} className="text-muted" aria-hidden />
          )}
        </div>

        <h5 className="collection-modal-section-title">
          Product information
        </h5>
        <div className="collection-modal-spec-grid">
          <SpecRow
            label="Item No"
            value={
              itemNo != null && itemNo !== '' ? String(itemNo) : 'N/A'
            }
          />
          <SpecRow
            label="UID"
            value={
              product.uid != null && product.uid !== ''
                ? String(product.uid)
                : 'N/A'
            }
          />
          <SpecRow label="Category" value={category} />
          <SpecRow label="Product name" value={productName} />
        </div>

        <h5 className="collection-modal-section-title">Specifications</h5>
        <div className="collection-modal-spec-grid">
          <SpecRow label="HS code" value={hsCode} />
          <SpecRow label="Suitable for" value={suitableFor} />
          <SpecRow label="Dimensions (L × W × H)" value={dimensionsLine} />
          <SpecRow label="Net weight" value={weightLine} />
          <SpecRow label="UOM" value={uomDisplay} />
          <SpecRow label="Colors" value={colors} />
          <SpecRow label="Finishes" value={finishes} />
          <SpecRow label="Materials" value={materials} />
          <SpecRow label="Collection tags" value={collectionsList} />
          <SpecRow label="Pack types" value={packTypes} />
        </div>

        {showPackingSection && (
          <>
            <h5 className="collection-modal-section-title">
              Packing details
            </h5>
            {innerPackLines && innerPackLines.length > 0 && (
              <>
                <p className="collection-modal-pack-subtitle">Inner packs</p>
                <ol className="collection-modal-pack-list">
                  {innerPackLines.map((line, i) => (
                    <li key={`inner-${i}`}>{line}</li>
                  ))}
                </ol>
              </>
            )}
            {masterPackLines && masterPackLines.length > 0 && (
              <>
                <p className="collection-modal-pack-subtitle">Master packs</p>
                <ol className="collection-modal-pack-list">
                  {masterPackLines.map((line, i) => (
                    <li key={`master-${i}`}>{line}</li>
                  ))}
                </ol>
              </>
            )}
          </>
        )}

        <h5 className="collection-modal-section-title">Commercial</h5>
        <div className="collection-modal-spec-grid">
          <SpecRow
            label="MOQ"
            value={
              product.moq != null && product.moq !== ''
                ? String(product.moq)
                : null
            }
          />
          <SpecRow
            label="Cost"
            value={
              product.cost != null && product.cost !== ''
                ? `₹ ${product.cost}`
                : null
            }
          />
        </div>

        {product.remark != null && String(product.remark).trim() !== '' && (
          <>
            <h5 className="collection-modal-section-title">Remarks</h5>
            <div className="collection-modal-remark">{product.remark}</div>
          </>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            padding: '10px',
            marginTop: '20px',
            backgroundColor: '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
